export interface Incident {
  id: string;
  incidentNumber: string;
  title: string;
  severity: 'Critical' | 'High' | 'Medium' | 'Low' | 'Informational';
  status: 'New' | 'Active' | 'Closed' | 'Redirected';
  assignedTo: string;
  createdTime: string;
  lastUpdateTime: string;
  classification?: string;
  classificationComment?: string;
  determination?: string;
  description?: string;
  alertsCount?: number;
  mitreTactics?: string[];
  entities?: (string | { type: string; name: string; verdict?: string; sha256?: string })[];
  /** Number of duplicate incidents collapsed under this one (server-side dedup). */
  dedupCount?: number;
  /** Azure Sentinel ARM incident GUID (IncidentName from KQL / resource name from ARM) */
  armIncidentName?: string;
  /** Graph API incident id — used for Graph PATCH when source is microsoft_graph_api */
  graphIncidentId?: string;
  webUrl?: string;
  /** Incident provider/source as returned by Sentinel/Defender, e.g. Microsoft XDR or Microsoft Sentinel. */
  providerName?: string;
  /** Product/source backing the first correlated alert where available. */
  providerProductName?: string;
  /** ProductName values from correlated SecurityAlert rows. Used for the Alert Provider column. */
  alertProviderNames?: string[];
  /** Compact display label derived from alertProviderNames. */
  alertProviderName?: string;
  /** User labels/tags attached to the incident in Sentinel/Defender. */
  labels?: string[];
  tags?: string[];
  entityCount?: number;
  /** Set when incidents from multiple tenants are merged in IncidentsTab. */
  _customerId?: string;
  /** SOC L1 auto-investigation status, populated by /dashboard-data when the
   *  background investigator has run for this incident. `null` while no
   *  investigation has been kicked off yet. */
  autoInvestigation?: {
    status: 'pending' | 'running' | 'posted' | 'completed' | 'failed' | 'skipped';
    startedAt?: string | null;
    finishedAt?: string | null;
    commentId?: string | null;
    summary?: string | null;
    sessionId?: string | null;
    incidentSignature?: string;
    verdict?: { verdict: 'TRUE POSITIVE' | 'FALSE POSITIVE' | 'BENIGN POSITIVE'; confidence: 'Low' | 'Medium' | 'High'; rationale: string } | null;
    /** Non-fatal delivery warning or terminal run error. */
    error?: string | null;
  } | null;
  /** Autonomous Sentinel-Incident Triage Agent verdict, populated by
   *  /dashboard-data when the background triage worker has produced a
   *  verdict for this incident's current signature. `null` while no
   *  verdict has been generated yet. */
  triage?: TriageSummary | null;
}

export type TriageVerdict =
  | 'TRUE POSITIVE'
  | 'FALSE POSITIVE'
  | 'BENIGN POSITIVE'
  | 'INCONCLUSIVE';
export type TriageRecommendedAction =
  | 'close_benign'
  | 'close_false_positive'
  | 'close_true_positive'
  | 'escalate_to_l2'
  | 'escalate_to_l3'
  | 'wait_for_more_signal';

export interface TriageEvidenceItem {
  /** Short tag — "alert" | "signin" | "process" | "network" | "entity" | "context" */
  kind: string;
  /** Human-readable headline (≤120 chars). */
  label: string;
  /** Specific concrete detail (≤300 chars). */
  detail: string;
}

export interface TriageVerdictPayload {
  verdict: TriageVerdict;
  confidence: number; // 1-100
  summary: string;
  recommendedAction: TriageRecommendedAction;
  keyEvidence: TriageEvidenceItem[];
}

export interface TriageSummary {
  rowId: string;
  customerId: string;
  incidentId: string;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'auto_closed' | 'skipped';
  signature: string;
  autoClosed: boolean;
  closedClassification: string | null;
  verdict: TriageVerdictPayload | null;
  promptVersion: string | null;
  aiModel: string | null;
  enqueuedAt: string;
  finishedAt: string | null;
  error: string | null;
}

export interface Case {
  id: number;
  title: string;
  priority: 'Critical' | 'High' | 'Medium' | 'Low' | 'Very low';
  status: 'New' | 'Open' | 'Closed';
  assignedTo: string;
  description: string;
  incidentIds: string;
  createdAt: string;
}

export interface DailyAlert {
  date: string;
  count: number;
}

export interface ThreatArticle {
  id: string;
  title: string;
  severity: 'Critical' | 'High' | 'Medium' | 'Info';
  createdDateTime: string;
  tags: string[];
  url: string;
  /** Original publisher (e.g. "Cyble", "SOCRadar"). Optional for back-compat with demo data. */
  source?: string;
}

export const KQL_TEMPLATES = [
  {
    label: 'Incidents by severity (7d)',
    value: 'SecurityIncident\n| where TimeGenerated > ago(7d)\n| summarize arg_max(TimeGenerated, *) by IncidentNumber\n| summarize count() by Severity\n| order by count_ desc',
  },
  {
    label: 'Open incidents (latest state)',
    value: 'SecurityIncident\n| summarize arg_max(TimeGenerated, *) by IncidentNumber\n| where Status in ("Active", "New")\n| project IncidentNumber, Title, Severity, Status, CreatedTime, Owner\n| order by CreatedTime desc\n| take 50',
  },
  {
    label: 'Closed incidents (7d)',
    value: 'SecurityIncident\n| where TimeGenerated > ago(7d)\n| summarize arg_max(TimeGenerated, *) by IncidentNumber\n| where Status == "Closed"\n| project IncidentNumber, Title, Severity, Classification, CreatedTime\n| order by CreatedTime desc\n| take 50',
  },
  {
    label: 'Top alerts (7d)',
    value: 'SecurityAlert\n| where TimeGenerated > ago(7d)\n| summarize AlertCount=count() by AlertName, AlertSeverity\n| order by AlertCount desc\n| take 20',
  },
  {
    label: 'Alert volume by day (30d)',
    value: 'SecurityAlert\n| where TimeGenerated > ago(30d)\n| summarize AlertCount=count() by Day=startofday(TimeGenerated)\n| order by Day asc',
  },
  {
    label: 'Top IPs in alerts (7d)',
    value: 'SecurityAlert\n| where TimeGenerated > ago(7d)\n| mv-expand Entity = parse_json(Entities)\n| where Entity.Type == "ip"\n| summarize AlertCount=count() by IPAddress=tostring(Entity.Address)\n| order by AlertCount desc\n| take 20',
  },
  {
    label: 'Incident trend (90d, daily)',
    value: 'SecurityIncident\n| where TimeGenerated > ago(90d)\n| summarize IncidentCount=count() by Day=bin(CreatedTime, 1d)\n| order by Day asc',
  },
  {
    label: 'MITRE tactics (30d)',
    value: 'SecurityIncident\n| where TimeGenerated > ago(30d)\n| summarize arg_max(TimeGenerated, *) by IncidentNumber\n| mv-expand todynamic(AdditionalData.tactics)\n| summarize count() by Tactic=tostring(todynamic_1)\n| order by count_ desc',
  },
  {
    label: 'Tables in workspace',
    value: 'search *\n| summarize count() by $table\n| order by count_ desc',
  },
  {
    label: 'Failed sign-ins (7d). Requires Entra ID P1.',
    value: 'SigninLogs\n| where TimeGenerated > ago(7d)\n| where ResultType != "0"\n| summarize FailureCount=count() by UserPrincipalName, IPAddress, ResultType\n| order by FailureCount desc\n| take 20',
  },
];

// ── Curated hunt templates organized by MITRE tactic / use-case ────────────
// Surfaced in the Data Lake KQL console as a categorized library so analysts
// can launch common investigations in one click.

export interface HuntTemplate {
  label: string;
  description: string;
  query: string;
}

export interface HuntCategory {
  label: string;
  templates: HuntTemplate[];
}

export const HUNT_TEMPLATES: HuntCategory[] = [
  {
    label: 'Initial Access',
    templates: [
      {
        label: 'Risky sign-ins (7d)',
        description: 'Entra ID sign-ins flagged at medium/high risk by Identity Protection.',
        query: 'SigninLogs\n| where TimeGenerated > ago(7d)\n| where RiskLevelDuringSignIn in ("medium", "high") or RiskLevelAggregated in ("medium", "high")\n| project TimeGenerated, UserPrincipalName, IPAddress, Location, RiskLevelDuringSignIn, RiskState, AppDisplayName\n| order by TimeGenerated desc\n| take 100',
      },
      {
        label: 'Impossible travel sign-ins (7d)',
        description: 'Successful sign-ins from >2 countries within 24h for the same user.',
        query: 'SigninLogs\n| where TimeGenerated > ago(7d) and ResultType == "0"\n| extend Country = tostring(LocationDetails.countryOrRegion)\n| where isnotempty(Country)\n| summarize Countries = make_set(Country), Count = count() by UserPrincipalName, bin(TimeGenerated, 1d)\n| where array_length(Countries) > 2\n| project Day=TimeGenerated, UserPrincipalName, Countries, Count\n| order by Day desc',
      },
      {
        label: 'Failed sign-ins by IP (24h)',
        description: 'IPs producing a high volume of failed Entra ID sign-ins (possible password spray).',
        query: 'SigninLogs\n| where TimeGenerated > ago(24h) and ResultType != "0"\n| summarize FailureCount = count(), Users = dcount(UserPrincipalName) by IPAddress, Location\n| where FailureCount > 25 or Users > 10\n| order by FailureCount desc\n| take 50',
      },
    ],
  },
  {
    label: 'Execution & Persistence',
    templates: [
      {
        label: 'PowerShell encoded commands (7d)',
        description: 'Defender for Endpoint process events with -EncodedCommand or base64 payloads.',
        query: 'DeviceProcessEvents\n| where TimeGenerated > ago(7d)\n| where FileName in~ ("powershell.exe", "pwsh.exe")\n| where ProcessCommandLine has_any ("-enc ", "-EncodedCommand", "-e ", "FromBase64String")\n| project TimeGenerated, DeviceName, AccountName, InitiatingProcessFileName, ProcessCommandLine\n| order by TimeGenerated desc\n| take 100',
      },
      {
        label: 'New scheduled tasks (7d)',
        description: 'schtasks.exe / at.exe creations. Common persistence mechanism.',
        query: 'DeviceProcessEvents\n| where TimeGenerated > ago(7d)\n| where FileName in~ ("schtasks.exe", "at.exe")\n| where ProcessCommandLine has_any ("/create", "-create")\n| project TimeGenerated, DeviceName, AccountName, InitiatingProcessFileName, ProcessCommandLine\n| order by TimeGenerated desc\n| take 100',
      },
      {
        label: 'Run-key registry persistence (7d)',
        description: 'Writes to HKLM/HKCU Run keys and Startup folders.',
        query: 'DeviceRegistryEvents\n| where TimeGenerated > ago(7d)\n| where ActionType == "RegistryValueSet"\n| where RegistryKey has_any ("\\\\CurrentVersion\\\\Run", "\\\\CurrentVersion\\\\RunOnce", "\\\\Winlogon\\\\Shell")\n| project TimeGenerated, DeviceName, RegistryKey, RegistryValueName, RegistryValueData, InitiatingProcessFileName\n| order by TimeGenerated desc\n| take 100',
      },
    ],
  },
  {
    label: 'Lateral Movement',
    templates: [
      {
        label: 'Remote logons by IP (7d)',
        description: 'Successful interactive/RDP logons grouped by source IP.',
        query: 'DeviceLogonEvents\n| where TimeGenerated > ago(7d)\n| where ActionType == "LogonSuccess" and LogonType in ("RemoteInteractive", "Network")\n| summarize Devices = dcount(DeviceName), Users = dcount(AccountName), Logons = count() by RemoteIP\n| where isnotempty(RemoteIP)\n| order by Logons desc\n| take 50',
      },
      {
        label: 'PsExec / WMIC usage (7d)',
        description: 'Common admin tools used for lateral movement.',
        query: 'DeviceProcessEvents\n| where TimeGenerated > ago(7d)\n| where FileName in~ ("psexec.exe", "psexec64.exe", "wmic.exe", "paexec.exe")\n   or InitiatingProcessFileName in~ ("psexec.exe", "psexec64.exe")\n| project TimeGenerated, DeviceName, AccountName, FileName, ProcessCommandLine, InitiatingProcessCommandLine\n| order by TimeGenerated desc\n| take 100',
      },
    ],
  },
  {
    label: 'Defense Evasion',
    templates: [
      {
        label: 'Defender tampering attempts (7d)',
        description: 'Disable AV, real-time protection, or AMSI bypasses.',
        query: 'DeviceProcessEvents\n| where TimeGenerated > ago(7d)\n| where ProcessCommandLine has_any ("Set-MpPreference", "DisableRealtimeMonitoring", "amsiInitFailed", "Add-MpPreference -ExclusionPath")\n| project TimeGenerated, DeviceName, AccountName, FileName, ProcessCommandLine\n| order by TimeGenerated desc\n| take 100',
      },
      {
        label: 'Unsigned binaries from temp paths (7d)',
        description: 'Process executions from %TEMP% / %APPDATA% with no signer.',
        query: 'DeviceProcessEvents\n| where TimeGenerated > ago(7d)\n| where FolderPath has_any ("\\\\Temp\\\\", "\\\\AppData\\\\Local\\\\Temp\\\\", "\\\\AppData\\\\Roaming\\\\")\n| where isempty(InitiatingProcessSignerType) or InitiatingProcessSignerType == "Unsigned"\n| project TimeGenerated, DeviceName, AccountName, FileName, FolderPath, ProcessCommandLine\n| order by TimeGenerated desc\n| take 100',
      },
    ],
  },
  {
    label: 'Credential Access',
    templates: [
      {
        label: 'LSASS access events (7d)',
        description: 'Processes opening LSASS for memory read. Credential dumping signal.',
        query: 'DeviceEvents\n| where TimeGenerated > ago(7d)\n| where ActionType == "OpenProcessApiCall" and FileName =~ "lsass.exe"\n| project TimeGenerated, DeviceName, InitiatingProcessFileName, InitiatingProcessCommandLine, AccountName\n| order by TimeGenerated desc\n| take 100',
      },
      {
        label: 'Mimikatz / credential dumping tools (7d)',
        description: 'Known credential dumpers by name or command-line keywords.',
        query: 'DeviceProcessEvents\n| where TimeGenerated > ago(7d)\n| where FileName in~ ("mimikatz.exe", "procdump.exe", "wce.exe")\n   or ProcessCommandLine has_any ("sekurlsa::", "lsadump::", "kerberos::", "privilege::debug")\n| project TimeGenerated, DeviceName, AccountName, FileName, ProcessCommandLine\n| order by TimeGenerated desc\n| take 100',
      },
    ],
  },
  {
    label: 'Discovery',
    templates: [
      {
        label: 'Recon tooling (7d)',
        description: 'whoami / net / nltest / adfind activity bursts.',
        query: 'DeviceProcessEvents\n| where TimeGenerated > ago(7d)\n| where FileName in~ ("whoami.exe", "net.exe", "net1.exe", "nltest.exe", "adfind.exe", "bloodhound.exe")\n| summarize Count = count(), Tools = make_set(FileName) by DeviceName, AccountName, bin(TimeGenerated, 1h)\n| where Count > 10\n| order by Count desc\n| take 100',
      },
    ],
  },
  {
    label: 'Command & Control',
    templates: [
      {
        label: 'Outbound connections to rare IPs (7d)',
        description: 'Network connections to IPs seen <5 times across the org.',
        query: 'DeviceNetworkEvents\n| where TimeGenerated > ago(7d) and ActionType == "ConnectionSuccess"\n| where isnotempty(RemoteIP) and not(ipv4_is_private(RemoteIP))\n| summarize ConnectionCount = count(), Devices = dcount(DeviceName) by RemoteIP, RemotePort\n| where Devices < 5 and ConnectionCount < 50\n| order by ConnectionCount desc\n| take 100',
      },
      {
        label: 'DNS to suspicious TLDs (7d)',
        description: 'DNS lookups against newly-abused or no-cost TLDs.',
        query: 'DeviceNetworkEvents\n| where TimeGenerated > ago(7d) and ActionType == "ConnectionSuccess"\n| where RemoteUrl matches regex @"\\.(top|xyz|tk|cn|ru|gq|ml|cf|pw)$"\n| project TimeGenerated, DeviceName, RemoteUrl, RemoteIP, InitiatingProcessFileName\n| order by TimeGenerated desc\n| take 100',
      },
    ],
  },
  {
    label: 'Exfiltration & Impact',
    templates: [
      {
        label: 'Mass file modifications (1h). Possible ransomware.',
        description: 'Devices renaming/encrypting >100 files within an hour.',
        query: 'DeviceFileEvents\n| where TimeGenerated > ago(1h)\n| where ActionType in ("FileRenamed", "FileModified", "FileCreated")\n| summarize ModifiedFiles = dcount(FileName) by DeviceName, InitiatingProcessFileName, bin(TimeGenerated, 5m)\n| where ModifiedFiles > 100\n| order by ModifiedFiles desc\n| take 100',
      },
      {
        label: 'Large outbound transfers (24h)',
        description: 'Top processes/destinations by outbound connection volume.',
        query: 'DeviceNetworkEvents\n| where TimeGenerated > ago(24h) and ActionType == "ConnectionSuccess"\n| where isnotempty(RemoteIP) and not(ipv4_is_private(RemoteIP))\n| summarize Connections = count(), Devices = dcount(DeviceName) by InitiatingProcessFileName, RemoteIP, RemotePort\n| where Connections > 50\n| order by Connections desc\n| take 50',
      },
    ],
  },
];
